/**
 * Converts a time string in 24-hour format to 12-hour format.
 *
 * @param time - The time string in 24-hour format.
 * @returns The time string in 12-hour format, or an empty string if the input is invalid.
 */
export function to12HourFormat(time: string) {
  if (!time) return "";
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12; // Convert 0 -> 12
  return `${formattedHours}:${String(minutes).padStart(2, "0")} ${period}`;
}

/**
 * Converts a time string in 12-hour format to 24-hour format.
 *
 * @param time12 - The time string in 12-hour format.
 * @returns The time string in 24-hour format, or an empty string if the input is invalid.
 */
export function to24HourFormat(time12: string) {
  const match = time12.match(/(\d+):(\d+) (AM|PM)/);
  if (!match) return "";
  let [_, hours = "0", minutes = "0", period = "AM"] = match;
  let hours24 = parseInt(hours);
  if (period === "PM" && hours24 !== 12) hours24 += 12;
  if (period === "AM" && hours24 === 12) hours24 = 0;
  return `${String(hours24).padStart(2, "0")}:${minutes}`;
}

/**
 * Parses a time string in 12-hour format and returns the 24-hour format.
 *
 * @param input - The time string to parse.
 * @returns The 24-hour format of the time string, or null if the input is invalid.
 */
export function parseTime(input: string): string | null {
  const match = input.trim().match(/^(\d{1,4})\s*(am|pm)?$/i);

  if (!match) return null; // Invalid input

  let [_, timeStr, meridian] = match;
  let time = parseInt(timeStr!, 10);

  // Validate number range
  if (time < 0 || time > 2359) return null;

  // Ensure it's a valid HHMM or HMM format
  if (time > 59 && time % 100 >= 60) return null;

  let hours = Math.floor(time / 100);
  let minutes = time % 100;

  if (meridian) {
    meridian = meridian.toLowerCase();
    if (hours === 12) hours = 0; // 12am = 00:xx, 12pm = 12:xx
    if (meridian === "pm") hours += 12;
  } else {
    // If no am/pm, assume 24-hour time is correctly input
    if (hours > 23) return null; // Prevent invalid 24-hour input
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Returns the current time in 24-hour format, rounded to the nearest 15 minutes.
 *
 * @returns The current time in 24-hour format, rounded to the nearest 15 minutes.
 */
export function getDefaultTime24() {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const hours = now.getHours();

  // Handle case where rounding minutes results in next hour
  const adjustedHours = roundedMinutes === 60 ? (hours + 1) % 24 : hours;
  const adjustedMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;

  return `${adjustedHours.toString().padStart(2, "0")}:${adjustedMinutes.toString().padStart(2, "0")}`;
}
